import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import Seo, { ORG_SCHEMA } from "../components/Seo";
import Hero from "../components/home/Hero";
import TiltServiceCard from "../components/home/TiltServiceCard";
import Approach from "../components/home/Approach";
import ToolsSection from "../components/home/ToolsSection";
import LearnerShowcase from "../components/home/LearnerShowcase";
import PostCard from "../components/common/PostCard";
import CallbackForm from "../components/forms/CallbackForm";
import { Reveal, SectionHead } from "../components/common/Section";
import { whatsappLink } from "../components/layout/WhatsAppButton";
import { TRUST_PILLARS } from "../data/content";
import { useSettings } from "../context/SettingsContext";
import { api } from "../lib/api";

const ABOUT_IMG = "https://static.prod-images.emergentagent.com/jobs/b4dad798-223e-4abe-ad56-1c6e877c5f2b/images/faf44793e5804afc8c1b343a72b6cc6e19df0584586b57fce1cad72545289efa.jpeg";

function Trust() {
  return (
    <section data-testid="trust-section" className="bg-background py-24 lg:py-32">
      <div className="container-q">
        <SectionHead eyebrow="Why people trust Quantora" title="Guidance you can understand, plans you can live with." />
        <div className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-slate-200 bg-slate-200 md:grid-cols-2 lg:grid-cols-4">
          {TRUST_PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08} className="group bg-white p-8 transition-colors duration-300 hover:bg-navy">
              <p.icon size={28} className="text-gold" />
              <h3 className="mt-6 text-lg font-bold text-navy transition-colors group-hover:text-white">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 transition-colors group-hover:text-slate-400">{p.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  const { services } = useSettings();
  return (
    <section data-testid="services-section" className="bg-slate-100/70 py-24 lg:py-32">
      <div className="container-q">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionHead eyebrow="Our Financial Services" title="Five disciplines. One coherent financial plan." />
          <Link to="/services" data-testid="services-view-all-link" className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-mint-600">View all services <ArrowRight size={16} /></Link>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => <TiltServiceCard key={s.slug} service={s} index={i} />)}
        </div>
      </div>
    </section>
  );
}

function AboutTeaser() {
  return (
    <section className="bg-background py-24 lg:py-32">
      <div className="container-q grid items-center gap-14 lg:grid-cols-2">
        <Reveal className="relative">
          <img src={ABOUT_IMG} alt="Quantora advisory office" loading="lazy" className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl" />
          <div className="glass-light absolute -bottom-6 right-6 max-w-[260px] rounded-2xl p-5 shadow-xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-gold-600">Our promise</p>
            <p className="mt-1 text-sm font-medium text-navy">No guaranteed-return claims. No product pushing. Just clarity.</p>
          </div>
        </Reveal>
        <div>
          <SectionHead eyebrow="About Quantora" title="Financial Intelligence Meets Your Future." text="Quantora is a financial analytics and solutions company focused on helping people make informed financial decisions through technology, research, education and personalized financial guidance." />
          <Link to="/about" data-testid="home-about-link" className="mt-8 inline-flex items-center gap-2 rounded-full border border-navy px-6 py-3 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white">Our story <ArrowRight size={16} /></Link>
        </div>
      </div>
    </section>
  );
}

function Insights() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {}); }, []);
  if (!posts.length) return null;
  return (
    <section data-testid="insights-section" className="bg-slate-100/70 py-24 lg:py-32">
      <div className="container-q">
        <SectionHead eyebrow="Blog & Market Insights" title="Research-led reading for better decisions." />
        <div className="mt-14 grid gap-6 md:grid-cols-3">{posts.map((p, i) => <PostCard key={p.slug} post={p} index={i} />)}</div>
      </div>
    </section>
  );
}

function CtaBand() {
  const { settings } = useSettings();
  return (
    <section data-testid="cta-section" className="grain relative overflow-hidden bg-navy py-24">
      <div className="aurora absolute -right-20 top-0 h-96 w-96 rounded-full bg-mint/15 blur-[120px]" />
      <div className="container-q relative grid gap-14 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <h2 className="text-3xl font-bold text-white sm:text-4xl">Start with a free, no-obligation consultation.</h2>
          <p className="mt-4 max-w-lg text-slate-400">Tell us where you are and where you want to be. We'll help you map the path, transparently.</p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <Link to="/book-consultation" data-testid="cta-book-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 text-sm font-semibold text-navy transition-transform hover:-translate-y-0.5">Book Your Free Consultation <ArrowRight size={16} /></Link>
            <a href={whatsappLink(settings?.whatsapp_number)} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-7 py-4 text-sm font-semibold text-white transition-colors hover:border-[#25D366] hover:text-[#25D366]"><MessageCircle size={16} /> Chat on WhatsApp</a>
          </div>
        </div>
        <div className="glass-dark rounded-3xl p-8">
          <h3 className="text-lg font-semibold text-white">Prefer a call back?</h3>
          <p className="mb-6 mt-1 text-sm text-slate-400">Leave your number and we'll call you.</p>
          <CallbackForm />
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo path="/" schema={[ORG_SCHEMA, { "@context": "https://schema.org", "@type": "WebSite", name: "Quantora", url: "https://quantora.co.in" }]} />
      <Hero />
      <Trust />
      <ServicesSection />
      <ToolsSection />
      <Approach />
      <AboutTeaser />
      <Insights />
      <LearnerShowcase />
      <CtaBand />
    </>
  );
}
